import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../UIControls/ConfirmationDialog';

const UnsavedChangesContext = createContext();

export const UnsavedChangesProvider = ({ children }) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [nextPath, setNextPath] = useState('');
    const [customMessage, setCustomMessage] = useState('');
    const navigate = useNavigate();
    const [onSave, setOnSave] = useState(null);
    const [onDiscard, setOnDiscard] = useState(null);

    // Original navigation-focused version
    const promptUnsavedChanges = useCallback(
        (navigateTo) => {
            if (hasUnsavedChanges) {
                setNextPath(navigateTo);
                setShowPrompt(true);
            } else {
                navigate(navigateTo);
            }
        },
        [hasUnsavedChanges, navigate],
    );

    // New action-focused version with optional custom message
    const promptBeforeAction = useCallback(
        (action, customMsg = null) => {
            if (hasUnsavedChanges) {
                setPendingAction(() => action);
                if (customMsg) setCustomMessage(customMsg);
                setShowPrompt(true);
            } else {
                action();
            }
        },
        [hasUnsavedChanges],
    );

    // const handleSave = useCallback(() => {
    //   if (onSaveCallback) {
    //     onSaveCallback();
    //     setHasUnsavedChanges(false);
    //   }
    // }, [onSaveCallback]);

    const clearPendingSaves = () => {
        try {
            localStorage.removeItem('pendingWhiteboardSave');
            localStorage.removeItem('pendingCustomisedHandoutSave');
            localStorage.removeItem('pendingCombinedHandoutSave');
            sessionStorage.removeItem('pendingUpgradeHandoutId');
            sessionStorage.removeItem('pendingUpgradeHandoutIdSetAt');
            sessionStorage.removeItem('pendingCustomisedHandoutSaveBackup');
            sessionStorage.removeItem('pendingCustomisedHandoutSaveBackupExpiresAt');
            sessionStorage.removeItem('restoredCustomisedHandoutState');
            sessionStorage.removeItem('forcePendingCustomisedHandoutId');
        } catch (error) {
            console.error('Failed to clear pending saves:', error);
        }
    };

    const executeAndNavigate = useCallback(() => {
        if (nextPath && onDiscard) onDiscard();
        clearPendingSaves();
        if (pendingAction) pendingAction();
        setPendingAction(null);
        setHasUnsavedChanges(false);
        setCustomMessage('');
        if (nextPath) navigate(nextPath);
    }, [nextPath, onDiscard, pendingAction, navigate]);

    return (
        <UnsavedChangesContext.Provider
            value={{
                setOnSave,
                setOnDiscard,
                hasUnsavedChanges,
                setHasUnsavedChanges,
                promptUnsavedChanges,
                promptBeforeAction,
                onConfirmNavigation: executeAndNavigate,
                onCancelNavigation: () => {
                    setPendingAction(null);
                    setNextPath('');
                    setCustomMessage('');
                    setShowPrompt(false);
                },
                showPrompt,
            }}
        >
            {children}
            <ConfirmationDialog
                open={showPrompt}
                handleClose={() => {
                    setPendingAction(null);
                    setNextPath('');
                    setCustomMessage('');
                    setShowPrompt(false);
                }}
                onConfirm={executeAndNavigate}
                onSave={onSave}
                title="Unsaved Changes"
                message={customMessage || 'You have unsaved changes. Are you sure you want to continue?'}
            />
        </UnsavedChangesContext.Provider>
    );
};

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);
export { UnsavedChangesContext };
